import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';
import { useTheme, useMediaQuery } from '@mui/material';
import { tableFontSize } from 'components/StyleConstant';
import ArrowBackIcon from '@mui/icons-material/KeyboardArrowLeft';
import ArrowForwardIcon from '@mui/icons-material/KeyboardArrowRight';
import { Box, Typography, Pagination, Select, MenuItem, PaginationItem, TablePagination as TPagination } from '@mui/material';

const TablePagination = ({ sx = {}, gotoPage, setPageSize, state, count }) => {
	const theme = useTheme();

	const isMediumOrSmall = useMediaQuery(theme.breakpoints.down('md'));

	const total = count || 0;
	const pageSize = state?.pageSize || 10;
	const pageIndex = state?.pageIndex || 0;

	const startRange = total === 0 ? 0 : pageIndex * pageSize + 1;
	const endRange = Math.min((pageIndex + 1) * pageSize, total);

	const pageCount = Math.ceil(total / pageSize);

	const handleChangePage = (event, newPage) => {
		gotoPage(newPage - 1);
	};

	const handleChangeRowsPerPage = (event) => {
		setPageSize(Number(event.target.value));
		gotoPage(0); // Reset to first page
	};

	return (
		<Box
			sx={{
				gap: 2,
				display: 'flex',
				flexWrap: 'wrap',
				padding: '6px 15px',
				alignItems: 'center',
				backgroundColor: '#f8f9fa',
				// border: '1px solid #E0E0E0',
				borderBottomLeftRadius: '4px',
				borderBottomRightRadius: '4px',
				justifyContent: { xs: 'center', md: 'space-between' },

				...sx,
			}}
		>
			<Typography
				variant="body2"
				color="#6c757d"
				sx={{ fontSize: tableFontSize?.cellSize || '14px', fontWeight: 500, display: { xs: 'none', md: 'block' } }}
			>
				Showing <strong>{startRange}</strong> to <strong>{endRange}</strong> of <strong>{total}</strong> results
			</Typography>

			<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
				<Box sx={{ alignItems: 'center', gap: 1, display: { xs: 'none', sm: 'flex' } }}>
					<Typography variant="body2" color="#6c757d" sx={{ fontSize: tableFontSize?.cellSize || '14px' }}>
						Rows per page:
					</Typography>

					<Select
						size="small"
						value={pageSize}
						variant="outlined"
						onChange={handleChangeRowsPerPage}
						sx={{
							height: 32,
							fontSize: '14px',
							// backgroundColor: '#fff',

							'.Mui-focused': { boxShadow: 'none' },
							'.MuiInputBase-input': { color: '#6c757d' },
							'&.MuiOutlinedInput-notchedOutline': { borderColor: '#6c757d' },
							'&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#6c757d' },
							'&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6c757d' },
						}}
					>
						{[15, 30, 50, 100].map((option) => (
							<MenuItem key={option} value={option}>
								{option}
							</MenuItem>
						))}
					</Select>
				</Box>

				<Pagination
					shape="rounded"
					color="primary"
					count={pageCount}
					page={pageIndex + 1}
					onChange={handleChangePage}
					siblingCount={isMediumOrSmall ? 0 : 1}
					renderItem={(item) => <PaginationItem slots={{ previous: ArrowBackIcon, next: ArrowForwardIcon }} {...item} />}
					sx={{
						'& .MuiPaginationItem-root': {
							border: 'none',
							fontWeight: 500,
							fontSize: '14px',
							color: '#6c757d',

							'&:hover': {
								backgroundColor: '#f5f5f5',
							},
						},

						'& .Mui-selected': {
							color: '#fff',
							fontWeight: 'bold',
							borderRadius: '4px',
							backgroundColor: '#0d6efd !important',
						},
					}}
				/>
			</Box>
		</Box>
	);
};

TablePagination.propTypes = {
	count: PropTypes.number,
	gotoPage: PropTypes.func,
	setPageSize: PropTypes.func,
	state: PropTypes.shape({
		pageIndex: PropTypes.number,
		pageSize: PropTypes.number,
	}),
};

export default TablePagination;

export const TranStackPagination = ({ sx = {}, table, persist = true }) => {
	const theme = useTheme();

	const isMediumOrSmall = useMediaQuery(theme.breakpoints.down('md'));

	const [searchParams, setSearchParams] = useSearchParams();

	const totalRows = table.getFilteredRowModel().rows.length;
	const { pageIndex, pageSize } = table.getState().pagination;

	const pageCount = Math.ceil(totalRows / pageSize);
	const endRange = Math.min((pageIndex + 1) * pageSize, totalRows);
	const startRange = totalRows === 0 ? 0 : pageIndex * pageSize + 1;

	const setQueryParams = (params) => {
		const updatedParams = new URLSearchParams(window.location.search);

		Object.entries(params).forEach(([key, value]) => {
			updatedParams.set(key, value);
		});

		setSearchParams(updatedParams, { replace: true });
	};

	const handleChangePage = (event, newPage) => {
		const pageIndexZeroBased = newPage - 1;

		if (persist) {
			table.setPageIndex(pageIndexZeroBased);
			setQueryParams({ page: pageIndexZeroBased });
		} else {
			table.setPageIndex(pageIndexZeroBased);
		}
	};

	const handleChangeRowsPerPage = (event) => {
		const size = Number(event.target.value);

		if (persist) {
			setQueryParams({
				size,
				page: 0,
			});

			table.setPageIndex(0);
			table.setPageSize(size);
		} else {
			table.setPageIndex(0);
			table.setPageSize(size);
		}
	};

	return (
		<Box
			sx={{
				gap: 2,
				display: 'flex',
				flexWrap: 'wrap',
				padding: '6px 15px',
				alignItems: 'center',
				backgroundColor: '#f8f9fa',
				borderBottomLeftRadius: '4px',
				borderBottomRightRadius: '4px',
				// justifyContent: 'space-between',
				justifyContent: { xs: 'center', md: 'space-between' },

				...sx,
			}}
		>
			<Typography variant="body2" color="#6c757d" sx={{ fontSize: '13px', fontWeight: 500, display: { xs: 'none', md: 'block' } }}>
				Showing <strong>{startRange}</strong> to <strong>{endRange}</strong> of <strong>{totalRows}</strong> results
			</Typography>

			<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
				<Box sx={{ alignItems: 'center', gap: 1, display: { xs: 'none', md: 'flex' } }}>
					<Typography variant="body2" color="#6c757d" sx={{ fontSize: '13px' }}>
						Rows per page:
					</Typography>

					<Select
						size="small"
						value={pageSize}
						variant="outlined"
						onChange={handleChangeRowsPerPage}
						sx={{
							height: 32,
							fontSize: '13px',
							backgroundColor: '#fff',

							'.Mui-focused': { boxShadow: 'none' },
							'.MuiInputBase-input': { color: '#6c757d' },
							'&.MuiOutlinedInput-notchedOutline': { borderColor: '#6c757d' },
							'&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BDBDBD' },
							'&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
						}}
						MenuProps={{
							PaperProps: { sx: { maxHeight: 300 } },
						}}
					>
						{[15, 30, 50, 100].map((option) => (
							<MenuItem key={option} value={option} sx={{ fontSize: '13px' }}>
								{option}
							</MenuItem>
						))}
					</Select>
				</Box>

				<Pagination
					shape="rounded"
					color="primary"
					count={pageCount}
					page={pageIndex + 1}
					onChange={handleChangePage}
					siblingCount={isMediumOrSmall ? 0 : 1}
					renderItem={(item) => <PaginationItem slots={{ previous: ArrowBackIcon, next: ArrowForwardIcon }} {...item} />}
					sx={{
						'& .MuiPaginationItem-root': {
							border: 'none',
							fontSize: '13px',
							fontWeight: 500,
							color: '#6c757d',

							'&:hover': { backgroundColor: '#f5f5f5' },
						},

						'& .Mui-selected': {
							color: '#fff',
							fontWeight: 'bold',
							borderRadius: '4px',
							backgroundColor: '#0d6efd !important',
						},
					}}
				/>
			</Box>
		</Box>
	);
};

// import PropTypes from 'prop-types';
// import { v4 as uuidv4 } from 'uuid';
// import { useSearchParams } from 'react-router-dom';
// import { tableFontSize } from 'components/StyleConstant';
// import { TablePagination as TPagination } from '@mui/material';

// const TablePagination = ({ sx = {}, gotoPage, setPageSize, state, count }) => {
// 	// console.log(`state`, JSON.stringify(state));
// 	const handleChangePage = (event, newPage) => {
// 		gotoPage(newPage);
// 	};

// 	const handleChangeRowsPerPage = (event) => {
// 		setPageSize(Number(event.target.value));
// 		gotoPage(0);
// 	};

// 	return (
// 		<TPagination
// 			count={count}
// 			component="div"
// 			page={state.pageIndex}
// 			rowsPerPage={state.pageSize}
// 			onPageChange={handleChangePage}
// 			rowsPerPageOptions={[15, 30, 50, 100]}
// 			onRowsPerPageChange={handleChangeRowsPerPage}
// 			SelectProps={{
// 				inputProps: {
// 					id: `pagination-select-${uuidv4()}`,
// 				},
// 			}}
// 			sx={{
// 				display: 'flex',
// 				justifyContent: 'center',

// 				'&.MuiToolbar-root': { pl: 1.5 },
// 				'& p': { margin: 0 },
// 				'&.MuiTablePagination-root': { color: 'white' },
// 				'.MuiTablePagination-selectIcon': { color: 'white' },
// 				'.MuiTablePagination-toolbar': { py: 0, pr: 3, minHeight: '36px' },
// 				'.MuiTablePagination-toolbar p': { fontSize: tableFontSize.cellSize },

// 				...sx,
// 			}}
// 		/>
// 	);
// };

// TablePagination.propTypes = {
// 	count: PropTypes.number,
// 	gotoPage: PropTypes.func,
// 	setPageSize: PropTypes.func,
// 	state: PropTypes.object,
// 	// pathname: PropTypes.string,
// };

// export default TablePagination;

// export const TranStackPagination = ({ sx = {}, table, persist = true }) => {
// 	const [searchParams, setSearchParams] = useSearchParams();

// 	// console.log('searchParams', searchParams);

// 	const setQueryParams = (params) => {
// 		const updatedParams = new URLSearchParams(window.location.search);

// 		Object.entries(params).forEach(([key, value]) => {
// 			updatedParams.set(key, value);
// 		});

// 		setSearchParams(updatedParams, { replace: true });
// 	};

// 	const handleChangePage = (event, newPage) => {
// 		if (persist) {
// 			table.setPageIndex(newPage);

// 			setQueryParams({ page: newPage });
// 		} else {
// 			table.setPageIndex(newPage);
// 		}
// 	};

// 	const handleChangeRowsPerPage = (event) => {
// 		const size = Number(event.target.value);

// 		if (persist) {
// 			setQueryParams({
// 				size,
// 				page: 0,
// 			});
// 		} else {
// 			table.setPageSize(size);
// 			table.setPageIndex(0);
// 		}
// 	};

// 	return (
// 		<TPagination
// 			component="div"
// 			onPageChange={handleChangePage}
// 			rowsPerPageOptions={[15, 30, 50, 100]}
// 			page={table.getState().pagination.pageIndex}
// 			onRowsPerPageChange={handleChangeRowsPerPage}
// 			count={table.getFilteredRowModel().rows.length}
// 			rowsPerPage={table.getState().pagination.pageSize}
// 			SelectProps={{
// 				inputProps: {
// 					id: `pagination-select-${table.getFilteredRowModel().rows.length}`,
// 				},
// 			}}
// 			sx={{
// 				display: 'flex',
// 				justifyContent: 'center',

// 				'&.MuiToolbar-root': { pl: 1.5 },
// 				'& p': { margin: 0 },
// 				'&.MuiTablePagination-root': { color: 'white' },
// 				'.MuiTablePagination-selectIcon': { color: 'white' },
// 				'.MuiTablePagination-toolbar': { py: 0, pr: 3, minHeight: '36px' },
// 				'.MuiTablePagination-toolbar p': { fontSize: '13px' },

// 				'@media (max-width: 479px)': {
// 					'.MuiTablePagination-toolbar': {
// 						px: '10px',
// 						py: 0,
// 						minHeight: '32px',
// 					},
// 					'.MuiTablePagination-input': {
// 						marginRight: '10px',
// 					},
// 					'.MuiTablePagination-actions': {
// 						marginLeft: '10px !important',
// 					},
// 				},

// 				...sx,
// 			}}
// 		/>
// 	);
// };
